/**
 * Page taxonomy — every route declares an archetype.
 *
 * See `.claude/docs/page-taxonomy.md` for the full convention. The
 * archetype constrains where a page can live (Hub = module landing,
 * Workspace = sidebar entry with tabs, Lens = NOT a sidebar entry,
 * Utility = settings/audit/debug only).
 *
 * The registry below is in-memory only — it's populated as modules
 * load. A future PR can introspect it to lint sidebar overflow
 * (>7 items per module) or auto-generate the Examples table in
 * page-taxonomy.md.
 */

export type PageArchetype = 'hub' | 'workspace' | 'lens' | 'utility';

export interface PageDefinition {
  /** Canonical route path, e.g. '/settings/members' or '/me/favorites'. */
  route: string;
  archetype: PageArchetype;
  /** Human-readable page title shown in PageHeader and breadcrumb. */
  title: string;
  /** Module slug this page belongs to (or 'shell' for app shell pages). */
  module: string;
  /** Parent route — set when this is a tab or lens inside a Workspace. */
  parent?: string;
  /** Shell this page renders inside. */
  shell?: 'app' | 'settings' | 'control';
  /** Sidebar group within the shell ('Account', 'Workspace', 'Platform', etc.). */
  group?: string;
}

const REGISTRY = new Map<string, PageDefinition>();

/**
 * Register a page in the in-memory registry. Idempotent — calling
 * twice with the same route overwrites (handy for HMR). Returns the
 * definition so it can be re-exported alongside the route component.
 *
 * Usage in a route file:
 *
 *   export const pageDef = definePage({
 *     route: '/settings/members',
 *     archetype: 'workspace',
 *     title: 'Members & Roles',
 *     module: 'shell',
 *     shell: 'settings',
 *     group: 'Workspace',
 *   });
 */
export function definePage(opts: PageDefinition): PageDefinition {
  REGISTRY.set(opts.route, opts);
  return opts;
}

/** Read-only view of every registered page. Intended for tooling, not runtime UI. */
export function listPages(): PageDefinition[] {
  return Array.from(REGISTRY.values());
}

/** Look up a registered page by its canonical route. */
export function getPage(route: string): PageDefinition | undefined {
  return REGISTRY.get(route);
}
